/**
 * Background Service for fetching and caching instruments data
 * Uses IndexedDB for persistent storage (survives browser close)
 */

import { apiClient } from '../api';
import { indexedDBService } from '../storage/IndexedDBService';
import type { Instrument } from '@ocktra/shared-types';

const INSTRUMENTS_CACHE_KEY = 'instruments_data';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const BACKGROUND_FETCH_INTERVAL = 30 * 60 * 1000; // 30 minutes (while app is open)

export class InstrumentsBackgroundService {
    private fetchInterval: number | null = null;
    private isFetching = false;
    private isInitialized = false;

    /**
     * Initialize the background service (IndexedDB only, no fetch)
     */
    async init(): Promise<void> {
        if (this.isInitialized) {
            return;
        }

        try {
            console.log('[InstrumentsService] Initializing IndexedDB...');

            // Initialize IndexedDB
            await indexedDBService.init();

            // Cleanup expired data on init
            indexedDBService.cleanupExpired().catch(err => {
                console.error('[InstrumentsService] Cleanup failed:', err);
            });

            this.isInitialized = true;
            console.log('[InstrumentsService] Background service initialized');
        } catch (error) {
            console.error('[InstrumentsService] Failed to initialize:', error);
            throw error;
        }
    }

    /**     * Trigger fetch when data provider is connected
     * This method is called when a data provider connection is established
     */
    async onDataProviderConnected(): Promise<void> {
        console.log('[InstrumentsService] Data provider connected, fetching instruments...');

        try {
            // Check if we have cached data
            const cachedData = await this.getCachedInstruments();

            if (!cachedData) {
                console.log('[InstrumentsService] No cached data found, fetching now...');
                await this.fetchAndCacheInstruments();
            } else {
                console.log('[InstrumentsService] Using cached instruments, refreshing in background...');
                // Fetch in background to update cache
                this.fetchAndCacheInstruments().catch(err => {
                    console.error('[InstrumentsService] Background fetch failed:', err);
                });
            }

            // Start background fetch interval if not already running
            if (!this.fetchInterval) {
                this.startBackgroundFetch();
            }
        } catch (error) {
            console.error('[InstrumentsService] Failed to fetch on provider connection:', error);
        }
    }

    /**     * Start background fetch interval
     */
    private startBackgroundFetch(): void {
        if (this.fetchInterval) {
            return;
        }

        this.fetchInterval = window.setInterval(() => {
            console.log('[InstrumentsService] Running background fetch...');
            this.fetchAndCacheInstruments().catch(err => {
                console.error('[InstrumentsService] Background fetch failed:', err);
            });
        }, BACKGROUND_FETCH_INTERVAL);

        console.log(`[InstrumentsService] Started background fetch (every ${BACKGROUND_FETCH_INTERVAL / 60000} minutes)`);
    }

    /**
     * Stop background fetch interval
     */
    stopBackgroundFetch(): void {
        if (this.fetchInterval) {
            clearInterval(this.fetchInterval);
            this.fetchInterval = null;
            console.log('[InstrumentsService] Stopped background fetch');
        }
    }

    /**
     * Fetch instruments from API and cache in IndexedDB
     */
    async fetchAndCacheInstruments(): Promise<Instrument[]> {
        if (this.isFetching) {
            console.log('[InstrumentsService] Fetch already in progress, skipping...');
            const cached = await this.getCachedInstruments();
            return cached || [];
        }

        try {
            this.isFetching = true;
            console.log('[InstrumentsService] Fetching instruments from API...');

            const response = await apiClient.get<{ data: Instrument[] }>('/instruments');

            if (!response.data || !Array.isArray(response.data)) {
                throw new Error('Invalid instruments data received');
            }

            const instruments = response.data;
            console.log(`[InstrumentsService] Fetched ${instruments.length} instruments`);

            // Cache in IndexedDB with 24-hour expiry
            await indexedDBService.set(
                'instruments',
                INSTRUMENTS_CACHE_KEY,
                instruments,
                CACHE_DURATION_MS
            );

            console.log('[InstrumentsService] Instruments cached successfully');
            return instruments;
        } catch (error) {
            console.error('[InstrumentsService] Failed to fetch instruments:', error);
            // Return cached data if available
            const cached = await this.getCachedInstruments();
            if (cached) {
                console.log('[InstrumentsService] Returning cached data due to fetch error');
                return cached;
            }
            throw error;
        } finally {
            this.isFetching = false;
        }
    }

    /**
     * Get cached instruments from IndexedDB
     */
    async getCachedInstruments(): Promise<Instrument[] | null> {
        try {
            const cached = await indexedDBService.get<Instrument[]>(
                'instruments',
                INSTRUMENTS_CACHE_KEY
            );

            if (cached) {
                console.log(`[InstrumentsService] Retrieved ${cached.length} cached instruments`);
            }

            return cached;
        } catch (error) {
            console.error('[InstrumentsService] Failed to get cached instruments:', error);
            return null;
        }
    }

    /**
     * Get instruments (from cache or fetch if needed)
     */
    async getInstruments(): Promise<Instrument[]> {
        // Try to get from cache first
        const cached = await this.getCachedInstruments();

        if (cached && cached.length > 0) {
            return cached;
        }

        // Fetch if no cache available
        return this.fetchAndCacheInstruments();
    }

    /**
     * Search instruments by symbol or name
     */
    async searchInstruments(query: string): Promise<Instrument[]> {
        const instruments = await this.getInstruments();
        const lowerQuery = query.toLowerCase();

        return instruments.filter(inst =>
            inst.tradingSymbol?.toLowerCase().includes(lowerQuery) ||
            inst.meta_data?.name?.toLowerCase().includes(lowerQuery) ||
            inst.additional_properties?.instrument_token?.toString().includes(query)
        );
    }

    /**
     * Get instrument by symbol
     */
    async getInstrumentBySymbol(symbol: string): Promise<Instrument | null> {
        const instruments = await this.getInstruments();
        return instruments.find(inst => inst.tradingSymbol === symbol) || null;
    }

    /**
     * Get instrument by token
     */
    async getInstrumentByToken(token: string): Promise<Instrument | null> {
        const instruments = await this.getInstruments();
        return instruments.find(inst => inst.additional_properties?.instrument_token === token) || null;
    }

    /**
     * Force refresh cache
     */
    async refreshCache(): Promise<void> {
        console.log('[InstrumentsService] Force refreshing cache...');
        await this.fetchAndCacheInstruments();
    }

    /**
     * Clear cached instruments
     */
    async clearCache(): Promise<void> {
        console.log('[InstrumentsService] Clearing cached instruments...');
        await indexedDBService.delete('instruments', INSTRUMENTS_CACHE_KEY);
    }

    /**
     * Get cache info (timestamp, size, etc.)
     */
    async getCacheInfo(): Promise<{
        exists: boolean;
        itemCount: number;
        timestamp: number | null;
        expiresAt: number | null;
        isExpired: boolean;
    }> {
        const cached = await this.getCachedInstruments();

        if (!cached) {
            return {
                exists: false,
                itemCount: 0,
                timestamp: null,
                expiresAt: null,
                isExpired: true
            };
        }

        // Get stored metadata
        const storedData = await indexedDBService.get<any>(
            'instruments',
            INSTRUMENTS_CACHE_KEY
        );

        return {
            exists: true,
            itemCount: cached.length,
            timestamp: storedData?.timestamp || null,
            expiresAt: storedData?.expiresAt || null,
            isExpired: false // If we got data, it's not expired
        };
    }

    /**
     * Cleanup - call this when app is closing
     */
    destroy(): void {
        this.stopBackgroundFetch();
        this.isInitialized = false;
        console.log('[InstrumentsService] Background service destroyed');
    }
}

// Export singleton instance
export const instrumentsService = new InstrumentsBackgroundService();
